import React, { memo, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Table } from 'antd'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import { TemporaryDataType, TemporarySetDataType } from './useTemporaryDataState'
import { pipeMovementColumns } from './static-data-columns'
import { EditableCell, EditableRow } from './editable-table-utils'
import { CustomDateTimePicker } from './components/CustomDateTimePicker'
import { emptyPipeMovements } from '../utils/emptyEntities'
import { CustomTableHeader } from './components/CustomTableHeader'
import { fetchKpiGoalsWithFilter, saveAllTemporaryData } from '../importer-api'
import { deleteEntityAndItsChildren, deletedCascadeEntities } from '../utils/actions'
import { generateRestrictedGoalValue, getFormattedGoalLabel, normalizeTubularTypeValue } from '../utils/utils'
import { StaticDataImporterContext } from '../context/StaticDataImporterContext'
import { getSelectedRowKeys, useSelectAllRows } from '../hooks/useSelectAllRows'
import { useNavigateToNewRow } from '../hooks/useNavigateToNewRow'
import { RowSelectionType } from 'antd/lib/table/interface'
import { useMoveEntityRowToAnotherEntityRowParent } from '../hooks/useMoveEntityRowToAnotherEntityRowParent'

type EditableTableProps = Parameters<typeof Table>[0]

type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>

const TemporaryRestrictedPipeMovementTable = memo(
    ({
        parent,
        data,
        setData,
        optionsOfTubularTypeEntity,
        localErrors,
        removeErrorWhenCheckedRowIsRemoved
    }: {
        parent: TubularTypeDto
        data: TemporaryDataType
        setData: TemporarySetDataType
        optionsOfTubularTypeEntity: TubularTypeSelectOptions
        localErrors: LocalErrorsTypes[]
        removeErrorWhenCheckedRowIsRemoved: (value: TemporaryDataType) => void
    }): ReactElement => {
        const [startDate, setStartDate] = useState<Record<number, Date | string>>(null)
        const [endDate, setEndDate] = useState<Record<number, Date | string>>(null)
        const [speedyOptionsByRow, setSpeedyOptionsByRow] = useState<
            Record<
                number,
                {
                    label: string
                    value: ValueUom | string
                }[]
            >
        >({})

        const {
            checkedRows,
            setCheckedRows,
            formatCheckedRows,
            setIsValidToTransfer,
            updateValueFromCheckedEntity
        } = useContext(StaticDataImporterContext)

        const { selectAllRows } = useSelectAllRows()

        const { goToNewRow, classNameNewRow } = useNavigateToNewRow()
        const { moveRowToAnotherParent } = useMoveEntityRowToAnotherEntityRowParent()

        const pipeMovements = useMemo(
            () =>
                data.pipeMovements
                    .filter(pipe => pipe.parent == parent.nodeId)
                    .map(pipeMovement => ({
                        ...pipeMovement,
                        key: String(pipeMovement.rowId),
                        parent: parent.nodeId
                    })),
            [data.pipeMovements, parent.nodeId]
        )

        useEffect(() => {
            const newStartDate: Record<number, Date | string> = {}
            const newEndDate: Record<number, Date | string> = {}
            for (const pipeMovement of data.pipeMovements) {
                newStartDate[pipeMovement.rowId] = pipeMovement.start ? new Date(pipeMovement.start) : ''
                newEndDate[pipeMovement.rowId] = pipeMovement.end ? new Date(pipeMovement.end) : ''

                if (pipeMovement?.restrictedPipeMovementSpeedValue) {
                    pipeMovement.restrictedPipeMovementSpeed = {
                        value: pipeMovement.restrictedPipeMovementSpeedValue,
                        name: pipeMovement.restrictedPipeMovementSpeedUom,
                        id: 0,
                        attribute_id: null,
                        unitOfMeasure_id: null
                    }
                }
            }
            setStartDate(newStartDate)
            setEndDate(newEndDate)
        }, [parent.tubularType, optionsOfTubularTypeEntity?.tubularType, data.pipeMovements])

        useEffect(() => {
            // Load the Goal options for each existing pipe movement according to its own Movement Type.
            // Restricted-pipe-movement-type options load asynchronously in the parent, so this must re-run
            // once they arrive instead of only firing once on mount.
            pipeMovements.forEach(pipeMovement => {
                if (pipeMovement?.restrictedPipeMovementType) getKpiGoalOptionsWithFilter(pipeMovement)
            })
        }, [pipeMovements, getKpiGoalOptionsWithFilter])

        // Functions
        const getCurrentRigId = useCallback(
            (restrictedParent: number): string => {
                const runParent = data.runs.find(run => run.nodeId === restrictedParent).parent
                const sectionParent = data.sections.find(section => section.nodeId === runParent).parent
                const interventionParent = data.interventions.find(
                    intervention => intervention.nodeId === sectionParent
                ).parent
                const wellRigId = data.wells.find(well => well.nodeId === interventionParent).rigId

                return String(wellRigId)
            },
            [data.interventions, data.runs, data.sections, data.wells]
        )

        const getKpiGoalOptionsWithFilter = useCallback(
            (record: TubularRestrictedPipeMovementsDto): void => {
                const normalizedTubularType = normalizeTubularTypeValue(parent?.tubularType)
                const tubularType = (optionsOfTubularTypeEntity?.tubularType as RestrictedSelectOptions[])?.find(
                    type => type?.value === normalizedTubularType
                )?.acronym
                const restrictedPipeType = (optionsOfTubularTypeEntity?.restrictedPipeMovementType as RestrictedSelectOptions[])?.find(
                    type => type?.label === record?.restrictedPipeMovementType
                )?.acronym

                const filter = {
                    1: tubularType,
                    2: 'PMT',
                    7: restrictedPipeType
                }

                const rigId = getCurrentRigId(parent.parent)

                fetchKpiGoalsWithFilter(filter, rigId).then(res => {
                    const options = []
                    res.data.map(goal => {
                        const newOption = {
                            value: goal.target?.goals?.value,
                            uom: { name: goal.target?.goals?.uom },
                            speedAcronym: getFormattedGoalLabel(goal) ?? ''
                        }
                        options.push(generateRestrictedGoalValue((newOption as unknown) as ValueUom))
                    })
                    setSpeedyOptionsByRow(prev => ({ ...prev, [record.rowId]: options }))
                })
            },
            [
                getCurrentRigId,
                optionsOfTubularTypeEntity?.restrictedPipeMovementType,
                optionsOfTubularTypeEntity?.tubularType,
                parent.parent,
                parent?.tubularType
            ]
        )

        const handleEditData = useCallback(
            (
                entity: TubularRestrictedPipeMovementsDto[],
                row: TubularRestrictedPipeMovementsDto,
                setter: TemporarySetDataType
            ): void => {
                const newData = [...entity]
                const index = newData.findIndex(item => String(row.rowId) === String(item.rowId))
                const item = newData[index]
                if (item) {
                    saveAllTemporaryData(row, 'TUBULAR_RESTRICTED_PIPE_MOVEMENT').then(saves => {
                        row.rowId = saves[0].id
                        row.key = saves[0].id
                        newData.splice(index, 1, {
                            ...item,
                            ...row
                        })
                        setter.setPipeMovements(newData)
                        setIsValidToTransfer(false)
                        updateValueFromCheckedEntity(`pipeMovements=>${row.parent}`, row)
                    })
                }
            },
            [setIsValidToTransfer, updateValueFromCheckedEntity]
        )

        const handleEditSave = useCallback(
            (row: TubularRestrictedPipeMovementsDto): void => {
                handleEditData(data.pipeMovements, row, setData)
                getKpiGoalOptionsWithFilter(row)
            },
            [data.pipeMovements, getKpiGoalOptionsWithFilter, handleEditData, setData]
        )

        const setRowStartDate = useCallback(
            (rowId: number, newDate: Date): void => {
                // Changing entity row
                const newData = [...data.pipeMovements]
                const currentRow = newData.find(row => String(row.rowId) === String(rowId))
                currentRow.start = newDate ? new Date(newDate).getTime() : null
                handleEditSave(currentRow)
                // Changing startDate state
                const newStartDate = { ...startDate }
                newStartDate[rowId] = newDate
                setStartDate(newStartDate)
            },
            [data.pipeMovements, handleEditSave, startDate]
        )

        const setRowEndDate = useCallback(
            (rowId: number, newDate: Date): void => {
                // Changing entity row
                const newData = [...data.pipeMovements]
                const currentRow = newData.find(row => String(row.rowId) === String(rowId))
                currentRow.end = newDate ? new Date(newDate).getTime() : null
                handleEditSave(currentRow)
                // Changing endDate state
                const newEndDate = { ...endDate }
                newEndDate[rowId] = newDate
                setEndDate(newEndDate)
            },
            [data.pipeMovements, endDate, handleEditSave]
        )

        const handleAddNewRow = useCallback((): void => {
            const oldEntity = [...data.pipeMovements]

            const newEntity: TubularRestrictedPipeMovementsDto = {
                ...emptyPipeMovements,
                key: undefined,
                frontParentUuid: parent.frontUuid,
                parent: parent.nodeId,
                nodeId: Date.now(),
                rowId: undefined,
                frontUuid: uuidv4()
            }

            saveAllTemporaryData(newEntity, 'TUBULAR_RESTRICTED_PIPE_MOVEMENT').then(saves => {
                newEntity.rowId = saves[0].id
                newEntity.key = saves[0].id
                newEntity.newRow = true
                oldEntity.push(newEntity)
                setData.setPipeMovements(oldEntity)
                goToNewRow()
            })
        }, [data.pipeMovements, goToNewRow, parent.frontUuid, parent.nodeId, setData])

        const handleDelete = useCallback(
            (record: TubularRestrictedPipeMovementsDto): void => {
                const newData = data.pipeMovements.filter(item => String(item.rowId) !== String(record.rowId))
                const newCheckedRows = deletedCascadeEntities(checkedRows, record)

                deleteEntityAndItsChildren(record.rowId).then(() => {
                    const newCheckedToTransfer = formatCheckedRows(newCheckedRows)

                    setData.setPipeMovements(newData)
                    setCheckedRows(newCheckedRows)
                    removeErrorWhenCheckedRowIsRemoved(newCheckedToTransfer)
                })
            },
            [
                checkedRows,
                data.pipeMovements,
                formatCheckedRows,
                removeErrorWhenCheckedRowIsRemoved,
                setCheckedRows,
                setData
            ]
        )

        // Element components
        const handleCalendarElementStartDate = useCallback(
            (record: TubularRestrictedPipeMovementsDto): ReactElement => {
                if (!startDate) return null

                return (
                    <CustomDateTimePicker
                        date={startDate[record.rowId] as string}
                        setDate={setRowStartDate}
                        rowId={record.rowId}
                    />
                )
            },
            [setRowStartDate, startDate]
        )
        const handleCalendarElementEndDate = useCallback(
            (record: TubularRestrictedPipeMovementsDto): ReactElement => {
                if (!endDate) return null

                return (
                    <CustomDateTimePicker
                        date={endDate[record.rowId] as string}
                        setDate={setRowEndDate}
                        rowId={record.rowId}
                    />
                )
            },
            [endDate, setRowEndDate]
        )

        const components = useMemo(
            () => ({
                body: {
                    row: EditableRow,
                    cell: EditableCell
                }
            }),
            []
        )

        const pipeMovementCols = useMemo(
            () =>
                pipeMovementColumns(
                    handleDelete,
                    handleEditSave,
                    optionsOfTubularTypeEntity,
                    (entity: TubularRestrictedPipeMovementsDto, selectedParent: number) =>
                        moveRowToAnotherParent(entity, selectedParent, handleEditSave),
                    data.tubulars,
                    handleCalendarElementStartDate,
                    handleCalendarElementEndDate,
                    localErrors,
                    speedyOptionsByRow
                ),
            [
                data.tubulars,
                handleCalendarElementEndDate,
                handleCalendarElementStartDate,
                handleDelete,
                handleEditSave,
                localErrors,
                moveRowToAnotherParent,
                optionsOfTubularTypeEntity,
                speedyOptionsByRow
            ]
        )

        const title = useCallback((): ReactElement => {
            return <CustomTableHeader title="restricted pipe movements" handleAddNewRow={handleAddNewRow} />
        }, [handleAddNewRow])

        const rowSelection = useMemo(
            () => ({
                selectedRowKeys: getSelectedRowKeys(checkedRows, 'pipeMovements', parent),
                type: 'checkbox' as RowSelectionType,
                onChange: (selectedRowsKey: React.Key[], selectedRows: TubularRestrictedPipeMovementsDto[]) => {
                    selectAllRows({
                        data,
                        selectedRows,
                        parentNodeId: parent.nodeId,
                        type: 'pipeMovements'
                    })
                },
                onSelect: (
                    record: EntityDto,
                    selected: boolean,
                    selectedRows: TubularRestrictedPipeMovementsDto[],
                    nativeEvent: Event
                ) => {
                    selectAllRows({
                        data,
                        parentNodeId: parent.nodeId,
                        type: 'pipeMovements',
                        selectedRows,
                        target: record,
                        selected,
                        nativeEvent
                    })
                }
            }),
            [checkedRows, data, parent, selectAllRows]
        )

        const expandable = useMemo(
            () => ({
                defaultExpandAllRows: false
            }),
            []
        )

        return (
            <TableContainer>
                <Table
                    title={title}
                    size="small"
                    components={components}
                    rowClassName={(d: EntityDto) => (d?.newRow ? `editable-row ${classNameNewRow}` : 'editable-row')}
                    bordered
                    dataSource={pipeMovements}
                    columns={pipeMovementCols as ColumnTypes}
                    pagination={false}
                    rowSelection={rowSelection}
                    expandable={expandable}
                />
            </TableContainer>
        )
    }
)

TemporaryRestrictedPipeMovementTable.displayName = 'TemporaryRestrictedPipeMovementTable'

export default TemporaryRestrictedPipeMovementTable

const TableContainer = styled.main``
